import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AppShell } from '@/app/layouts';
import { CareStateProvider } from '@/entities/care-state-domain';
import { PatientEligibilityWarning } from '@/features/patient-eligibility-warning';
import { CareStateQuerySync } from '@/features/care-state-query-sync';
import { KarmaAwardToast } from '@/features/karma-award-feedback';
import {
  PatientGlobalSearchDialog,
  type PatientGlobalSearchHit,
} from '@/features/patient-global-search';
import { AgentTweakableStateProvider } from '@/features/copilot/agent-tweakable';
import { CopilotSessionProvider } from '@/features/copilot/lib';
import { selectPatientPanelTab } from '@/features/patient-workspace';
import { RequestsCompletionPrompt } from '@/features/requests-completion-prompt';
import { analytics } from '@/shared';
import {
  clearPatientWorkspaceSearchTarget,
  setPatientWorkspaceSearchTarget,
} from '@/shared/stores';
import { CopilotPanel } from '@/widgets/copilot-panel';
import { PatientPanel } from '@/widgets/patient-panel';

import { PatientNavigatorContent } from './PatientNavigatorContent';

/**
 * Patient Workspace Page
 *
 * Composition-only page that assembles the patient workspace layout.
 * This page composes layouts and widgets — it contains NO domain logic,
 * stores, or data fetching. All business logic lives in features/entities.
 *
 * Structure:
 * - AppShell: Resizable two-panel split (no global header)
 *   - Left panel: PatientPanel widget (title, tabs, content)
 *   - Right panel: CopilotPanel widget (tabs, thread, composer)
 *   - Left overlay: PatientNavigator (hover sidebar)
 */
function PatientWorkspacePage() {
  const { userId } = useParams<{ userId: string }>();
  const resolvedUserId = userId ?? '';
  const openedAtMsRef = useRef<number>(0);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);

  const handleOpenGlobalSearch = () => {
    if (resolvedUserId.trim() === '') {
      return;
    }
    setIsGlobalSearchOpen(true);
  };

  const handleSelectGlobalSearchHit = (hit: PatientGlobalSearchHit) => {
    selectPatientPanelTab(hit.tabId, { actor: 'clinician', patientUserId: resolvedUserId });
    if (hit.resultId !== undefined) {
      setPatientWorkspaceSearchTarget({
        tabId: hit.tabId,
        resource: hit.resource,
        query: hit.title,
        resultId: hit.resultId,
      });
      return;
    }
    clearPatientWorkspaceSearchTarget();
  };

  useEffect(() => {
    if (resolvedUserId.trim() === '') {
      return;
    }

    openedAtMsRef.current = Date.now();
    void analytics.track({
      name: 'workspace.patient.opened',
      context: {
        actor: 'clinician',
        patientUserId: resolvedUserId,
      },
    });

    return () => {
      const durationMs = Math.max(0, Date.now() - openedAtMsRef.current);
      void analytics.track({
        name: 'workspace.patient.closed',
        context: {
          actor: 'clinician',
          patientUserId: resolvedUserId,
        },
        properties: { durationMs },
      });
    };
  }, [resolvedUserId]);

  useEffect(() => {
    if (resolvedUserId.trim() === '') {
      return;
    }

    const onVisibilityChange = () => {
      const visibility = document.visibilityState === 'hidden' ? 'hidden' : 'visible';
      void analytics.track({
        name: 'workspace.visibility.changed',
        context: {
          actor: 'clinician',
          patientUserId: resolvedUserId,
        },
        properties: { visibility },
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [resolvedUserId]);

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) {
        return false;
      }

      const tagName = target.tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
        return true;
      }

      if (target.isContentEditable) {
        return true;
      }

      return target.closest('[contenteditable="true"]') !== null;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (resolvedUserId.trim() === '') {
        return;
      }
      if (event.key.toLowerCase() !== 'f') {
        return;
      }
      if (event.altKey || event.shiftKey) {
        return;
      }

      const isSearchShortcut = event.metaKey || event.ctrlKey;
      if (!isSearchShortcut) {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      setIsGlobalSearchOpen(true);
    };

    const options = { capture: true } as const;
    window.addEventListener('keydown', onKeyDown, options);
    return () => {
      window.removeEventListener('keydown', onKeyDown, options);
    };
  }, [resolvedUserId]);

  return (
    <CareStateProvider key={resolvedUserId || 'unknown-user'} patientUserId={resolvedUserId}>
      <CareStateQuerySync />
      <RequestsCompletionPrompt />
      <KarmaAwardToast />
      <PatientEligibilityWarning userId={resolvedUserId} />
      <AgentTweakableStateProvider>
        <CopilotSessionProvider key={resolvedUserId || 'unknown-user'} userId={resolvedUserId}>
          <AppShell
            persistenceId="patient-workspace"
            leftWorkspace={
              <PatientPanel
                userId={resolvedUserId}
                onOpenGlobalSearch={handleOpenGlobalSearch}
                globalSearchShortcutLabel="Cmd/Ctrl+F"
              />
            }
            rightCopilot={<CopilotPanel userId={resolvedUserId} />}
            leftOverlay={<PatientNavigatorContent activeUserId={userId} showFooter />}
          />
          <PatientGlobalSearchDialog
            open={isGlobalSearchOpen}
            onOpenChange={setIsGlobalSearchOpen}
            userId={resolvedUserId}
            onSelectHit={handleSelectGlobalSearchHit}
          />
        </CopilotSessionProvider>
      </AgentTweakableStateProvider>
    </CareStateProvider>
  );
}

export default PatientWorkspacePage;
